<script setup lang="ts">
import type { FeaturedVideosClusterCard } from '~/core/entities/property';

import {
	breakpointsTailwind,
	useBreakpoints,
	useIntersectionObserver
} from '@vueuse/core';
import { useEvents } from '~/composables/events/useEvents';
import {
	buildShortsFeedDestination,
	featuredVideosClusterFiltersToQuery,
	SHORTS_ENTRY_SOURCE,
	useFetchShortsClusters,
	useOpenShortsFeed
} from '~/composables/shorts';
import { MixpanelProps } from '~/core/service/mixpanel';
import { useStaleGuard } from '~/utils';

import ClusterCard from './ClusterCard.vue';
import SectionSkeleton from './SectionSkeleton.vue';

const props = defineProps<{
	referenceProperty?: string | null;
	region?: string | null;
}>();

const emit = defineEmits<{
	(e: 'update:has-clusters', value: boolean): void;
	(e: 'update:resolved', value: boolean): void;
}>();

const { fetchClusters } = useFetchShortsClusters();
const { openShortsFeed } = useOpenShortsFeed();
const { trackShortsClusterCardClicked } = useEvents();
const route = useRoute();
const fetchGuard = useStaleGuard();

const isLoading = ref(true);
const isResolved = ref(false);
const sectionTitle = ref<string | null>(null);
const clusters = ref<FeaturedVideosClusterCard[]>([]);

const displayedTitle = computed(
	() =>
		sectionTitle.value?.trim() ||
		`Imóveis parecidos${props.region?.trim() ? ` em ${props.region.trim()}` : ''}`
);

const sectionRef = ref<HTMLElement | null>(null);
const sliderRef = ref<{
	currentCarouselItemIndex: number;
} | null>(null);
const isMobileOrTablet = ref(false);
const isSectionVisible = ref(false);

function alignMobileOrTabletSlide(
	_viewSize: number,
	_snapSize: number,
	index: number
) {
	// After the first navigation, keep 20px of the previous card visible,
	// in addition to the 8px gap between cards.
	return index === 0 ? 0 : 28;
}

const carouselOptions = {
	cyclic: true,
	class: 'gap-7 md:gap-7',
	carouselWrapperClasses: '-mx-5 w-[calc(100%+2.5rem)] md:mx-0 md:w-full'
} as const;

const carouselBreakpoints = {
	'(max-width: 1023px)': {
		align: alignMobileOrTabletSlide,
		slidesToScroll: 1
	}
} as const;

async function loadClusters() {
	const capturedId = fetchGuard.next();
	isLoading.value = true;
	isResolved.value = false;
	emit('update:resolved', false);

	try {
		const response = await fetchClusters(props.referenceProperty);
		if (fetchGuard.isStale(capturedId)) return;

		sectionTitle.value = response.sectionTitle || null;
		clusters.value = response.clusters;
	} catch {
		if (fetchGuard.isStale(capturedId)) return;
		sectionTitle.value = null;
		clusters.value = [];
	} finally {
		if (!fetchGuard.isStale(capturedId)) {
			isLoading.value = false;
			isResolved.value = true;
			emit('update:has-clusters', clusters.value.length > 0);
			emit('update:resolved', true);
		}
	}
}

function handleClusterClick(cluster: FeaturedVideosClusterCard, index: number) {
	const queryParams = featuredVideosClusterFiltersToQuery(
		cluster.appliedFilters
	);
	const destinationUrl = buildShortsFeedDestination(
		cluster.targetCommercialId,
		queryParams,
		SHORTS_ENTRY_SOURCE.CLUSTER
	);

	trackShortsClusterCardClicked({
		[MixpanelProps.DESTINATION_URL]: destinationUrl,
		[MixpanelProps.CURRENT_PAGE]: route.fullPath,
		[MixpanelProps.SECTION_NAME]: 'clusters shorts',
		[MixpanelProps.CARD_POSITION]: index,
		[MixpanelProps.CARD_TYPE]: 'shorts card'
	});

	openShortsFeed({
		commercialId: cluster.targetCommercialId,
		clickedIndex: 0,
		queryParams,
		entrySource: SHORTS_ENTRY_SOURCE.CLUSTER
	});
}

onMounted(() => {
	const breakpoints = useBreakpoints(breakpointsTailwind);
	isMobileOrTablet.value = breakpoints.smaller('lg').value;
	watch(breakpoints.smaller('lg'), value => {
		isMobileOrTablet.value = value;
	});

	useIntersectionObserver(
		sectionRef,
		entries => {
			const entry = entries[0];
			if (entry) isSectionVisible.value = entry.isIntersecting;
		},
		{ threshold: 0.3 }
	);

	loadClusters();
});

watch(
	() => props.referenceProperty,
	(newReference, previousReference) => {
		if (import.meta.client && newReference !== previousReference)
			loadClusters();
	}
);
</script>

<template>
	<SectionSkeleton v-if="isLoading && !isResolved" />

	<section
		v-else-if="clusters.length"
		ref="sectionRef"
		class="w-full md:-mx-5 md:w-[calc(100%+2.5rem)] lg:mx-0 lg:w-full"
		aria-labelledby="property-shorts-clusters-title"
	>
		<StandardSlider
			ref="sliderRef"
			:items="clusters"
			:is-loading="false"
			carousel-item-class="first:ml-5 !basis-[44%] sm:!basis-[206px] lg:first:ml-0 lg:!basis-[calc(33.33333%-5.333px)] xl:!basis-[calc(25%-6px)]"
			:carousel-options="carouselOptions"
			:breakpoints="carouselBreakpoints"
			carousel-content-class="gap-2 lg:gap-2"
			container-class="px-5 py-10 md:px-0 md:py-11 lg:px-0"
			footer-class="gap-5 md:px-5 lg:px-0"
			selector-variant="dark"
			selector-arrows-variant="dark"
			:show-navigation-arrows="false"
			:limit-photos="false"
		>
			<template #header>
				<header
					class="flex flex-col items-start gap-1 px-0 pb-6 text-primary md:px-5 lg:px-0"
				>
					<h2
						id="property-shorts-clusters-title"
						class="text-2xl font-medium leading-[1.5]"
					>
						{{ displayedTitle }}
					</h2>
					<p class="text-lg font-normal leading-[1.5]">
						Explore opções similares em Shorts
					</p>
				</header>
			</template>

			<template #items="{ item, index }">
				<ClusterCard
					:cluster="item"
					:index="index"
					:is-active="
						isMobileOrTablet &&
						isSectionVisible &&
						sliderRef?.currentCarouselItemIndex === index
					"
					:hover-to-play="!isMobileOrTablet"
					@click="handleClusterClick(item, index)"
				/>
			</template>
		</StandardSlider>
	</section>
</template>
